import { getFlags } from '@/feature-flags';
import type { UserPolicy, UserPolicyDetails, UserPolicyTripDetails } from '@/types';
import axios from 'axios';
import { ROUTE_PATHS, ROUTES } from '../../config';
import { generateCorrelationIdForBFF } from './common';
import { compareDatesDesc } from './date';
import { getFrontendBuild, getServiceAPIBaseURL } from './env';

interface FetchUserPoliciesResponse {
  data?: UserPolicy[];
  error?: Error;
}

interface FetchPolicyWithNumberResponse {
  data?: UserPolicyDetails;
  error?: Error;
}

interface FilterSortPoliciesResponse {
  past: UserPolicy[];
  active: UserPolicy[];
  cancelled: UserPolicy[];
}

function sortPoliciesByDate(a: UserPolicy, b: UserPolicy) {
  return compareDatesDesc(a.policyEnd, b.policyEnd);
}

let fetchUserPoliciesController: AbortController | null = null;
async function fetchUserPolicies(): Promise<FetchUserPoliciesResponse> {
  const url = `${getServiceAPIBaseURL()}/userpolicies`;

  if (fetchUserPoliciesController) {
    fetchUserPoliciesController.abort();
    fetchUserPoliciesController = null;
  }

  try {
    fetchUserPoliciesController = new AbortController();
    const response = await axios.get<UserPolicy[]>(url, {
      signal: fetchUserPoliciesController.signal,
      withCredentials: true,
      headers: {
        'x-correlation-id': generateCorrelationIdForBFF(),
        x_cp_fe_build: getFrontendBuild(),
        'content-type': 'application/json',
        'x-csrf': '1',
      },
    });

    const { data } = response;
    if (!Array.isArray(data)) {
      return { error: new Error('Error retrieving policies.') };
    }

    return { data };
  } catch (error) {
    console.error(error);
    return { error: error as Error };
  }
}

let fetchPolicyWithNumberController: AbortController | null = null;
async function fetchPolicyWithNumber(policyNumber: string): Promise<FetchPolicyWithNumberResponse> {
  if (!policyNumber) {
    return { error: new Error('Invalid policy number.') };
  }

  const url = getBFFEndpointForPolicyNumber(policyNumber);
  if (fetchPolicyWithNumberController) {
    fetchPolicyWithNumberController.abort();
    fetchPolicyWithNumberController = null;
  }

  try {
    fetchPolicyWithNumberController = new AbortController();
    const response = await axios.get<UserPolicyTripDetails>(url, {
      signal: fetchPolicyWithNumberController.signal,
      withCredentials: true,
      headers: {
        'x-correlation-id': generateCorrelationIdForBFF(),
        x_cp_fe_build: getFrontendBuild(),
        'content-type': 'application/json',
        'x-csrf': '1',
      },
    });

    const { data } = response;
    if (!data || data.policyNumber !== policyNumber) {
      return { error: new Error(`Error retrieving policy: ${policyNumber}.`) };
    }

    const { trip, excess, planName, travellers, destinations, maxTripDuration } = data;

    return {
      data: {
        policyNumber,
        policyEnd: trip.endDate,
        policyStart: trip.startDate,
        type: trip.tripType.code === 'annualMultiTrip' ? 'Annual' : 'Single Trip',
        excess,
        planName,
        travellers,
        destinations,
        maxTripDuration,
      },
    };
  } catch (error) {
    console.error(error);
    return { error: error as Error };
  }
}

function filterSortPolicies(policies: UserPolicy[]): FilterSortPoliciesResponse {
  const response: FilterSortPoliciesResponse = { past: [], active: [], cancelled: [] };

  /**
   * The MVP currently does not contain any filters for policies (only has filters for claims).
   * But we're keeping filter/sort to be similar, to make it easier for us to add them after.
   * (See 'filterSortClaims' in '/utils/claims.ts')
   */
  policies.forEach(policy => {
    switch (policy.status) {
      case 'Cancelled': {
        response.cancelled.push(policy);
        return;
      }

      case 'Expired': {
        response.past.push(policy);
        return;
      }

      case 'Active': {
        response.active.push(policy);
        return;
      }

      default:
    }
  });

  return {
    past: [...response.past].sort(sortPoliciesByDate),
    active: [...response.active].sort(sortPoliciesByDate),
    cancelled: [...response.cancelled].sort(sortPoliciesByDate),
  };
}

function getBFFEndpointForPolicyNumber(policyNumber: string) {
  return `${getServiceAPIBaseURL()}/userpolicies/${policyNumber}`;
}

function getDocumentLinksForPolicy({ policyNumber }: UserPolicy) {
  return {
    primary: {
      id: 'policy-pds',
      target: '_blank',
      label: 'Read PDS',
      href: `${getBFFEndpointForPolicyNumber(policyNumber)}/pds`,
    },
    secondary: {
      id: 'policy-coi',
      target: '_blank',
      label: 'View Certificate of Insurance',
      href: `${getBFFEndpointForPolicyNumber(policyNumber)}/coi`,
    },
  };
}

// Unlike 'policyNumber', 'policyNo' can be 'number' or 'string.
function getPathForMakingNewClaim(policyNo: number | string) {
  return ROUTE_PATHS[ROUTES.NEW_CLAIM].replace(':policyNo', `${policyNo}`);
}

function getLegacyLinkForMakingNewClaim(policyNumber: string) {
  return `${getBFFEndpointForPolicyNumber(policyNumber)}/claims/redirect`;
}

function getViewManageLinksForPolicy(policy: UserPolicy) {
  const { showManageMyPoliciesButton, canMakeNewClaimInPortal } = getFlags();
  const { policyNumber } = policy;

  if (policy.status === 'Cancelled') {
    return undefined;
  }

  return {
    primary: {
      id: 'policy-make-claim',
      target: canMakeNewClaimInPortal ? '_self' : '_blank',
      label: 'Make a claim',
      href: canMakeNewClaimInPortal
        ? getPathForMakingNewClaim(policyNumber)
        : getLegacyLinkForMakingNewClaim(policyNumber),
    },
    secondary: showManageMyPoliciesButton
      ? {
          id: 'policy-manage',
          target: '_blank',
          label: 'Manage my policy',
          href: `${getBFFEndpointForPolicyNumber(policyNumber)}/redirect`,
        }
      : undefined,
  };
}

export {
  fetchPolicyWithNumber,
  fetchUserPolicies,
  filterSortPolicies,
  getDocumentLinksForPolicy,
  getLegacyLinkForMakingNewClaim,
  getPathForMakingNewClaim,
  getViewManageLinksForPolicy,
};
